import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

export default function SEO({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'website',
  canonical,
  jsonLd,
  noindex = false,
}: SEOProps) {
  useEffect(() => {
    const setMeta = (attrName: string, attrValue: string, content: string) => {
      let el = document.querySelector(`meta[${attrName}="${attrValue}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setTitle = (t: string) => {
      document.title = t;
    };

    const setCanonical = (href: string) => {
      let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    const setRobots = () => {
      let el = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', 'robots');
        document.head.appendChild(el);
      }
      el.setAttribute('content', noindex ? 'noindex, nofollow' : 'index, follow');
    };

    if (title) setTitle(title);
    if (description) setMeta('name', 'description', description);
    if (keywords) setMeta('name', 'keywords', keywords);
    if (ogTitle) setMeta('property', 'og:title', ogTitle);
    if (ogDescription) setMeta('property', 'og:description', ogDescription);
    if (ogImage) setMeta('property', 'og:image', ogImage);
    if (ogType) setMeta('property', 'og:type', ogType);
    if (canonical) setCanonical(canonical);
    setRobots();

    const existingScript = document.getElementById('page-jsonld');
    if (existingScript) existingScript.remove();

    if (jsonLd) {
      const script = document.createElement('script');
      script.id = 'page-jsonld';
      script.type = 'application/ld+json';
      const data = Array.isArray(jsonLd)
        ? jsonLd.map((item) => ({ '@context': 'https://schema.org', ...item }))
        : { '@context': 'https://schema.org', ...jsonLd };
      script.textContent = JSON.stringify(data);
      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById('page-jsonld');
      if (script) script.remove();
    };
  }, [title, description, keywords, ogTitle, ogDescription, ogImage, ogType, canonical, jsonLd, noindex]);

  return null;
}